import { useState, useEffect, useRef } from 'react';
import { getApplaussAuth } from '../utils/authManager';

// The events that mean "the stored user may have changed": a login/logout or a
// merge into local storage (`authChanged`), and a token refresh
// (`sessionRefreshed`). Named once so the add/remove pairs can't drift.
const AUTH_EVENTS = ['authChanged', 'sessionRefreshed'];

/**
 * Custom hook to manage authentication state and listen for session refresh events
 *
 * The API's `display_name` is null when the user has not set one, so the
 * email-prefix fallback is applied here — the single render-time place for it.
 * `userProfile` exposes `fullName` and `firstName`; it deliberately has no
 * `display_name` key, which used to mean "first token of the name" here and the
 * whole name on the wire.
 *
 * `storedDisplayName` is the same field **raw** — `null` when unset, never the
 * fallback. Anything that writes the name back reads that one: saving
 * `userProfile.fullName` would persist the email prefix as though the user had
 * chosen it. Both come off the same read, so an editable field and the header
 * can never show different names.
 *
 * Both are same-tab only: `authChanged`/`sessionRefreshed` are CustomEvents,
 * which don't cross tabs, and there is deliberately no `storage` listener here —
 * it fires for every key another tab writes, and `refreshAuth` rebuilds
 * `userProfile` on each call, so every consumer would re-render on writes that
 * cannot have changed auth state. Another tab's change lands on the next mount.
 *
 * @returns {Object} { isAuthenticated, userProfile, storedDisplayName, refreshAuth }
 */
export function useAuth() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userProfile, setUserProfile] = useState(null);
  const [storedDisplayName, setStoredDisplayName] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Function to check and update auth state from localStorage
  const refreshAuth = () => {
    const applaussAuth = getApplaussAuth();

    if (applaussAuth && !applaussAuth.isExpired) {
      const fullName = applaussAuth.user?.display_name || applaussAuth.user?.email?.split('@')[0] || '';
      const firstName = fullName.split(' ')[0];

      const profile = {
        firstName: firstName,
        fullName: fullName,
        email: applaussAuth.user?.email,
        id: applaussAuth.user?.id,
        picture: applaussAuth.user?.picture,
        role: applaussAuth.user?.role
      };

      setUserProfile(profile);
      setStoredDisplayName(applaussAuth.user?.display_name ?? null);
      setIsAuthenticated(true);
    } else {
      setUserProfile(null);
      setStoredDisplayName(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    // Check auth on mount
    refreshAuth();

    // Listen for auth changes (session refresh, login, logout) with debounce
    const handleAuthChange = () => {
      // Clear any pending refresh
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }

      // Debounce the refresh to avoid multiple rapid updates
      refreshTimeoutRef.current = setTimeout(() => {
        refreshAuth();
      }, 100); // 100ms debounce
    };

    AUTH_EVENTS.forEach((name) => window.addEventListener(name, handleAuthChange));

    // Cleanup
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
      AUTH_EVENTS.forEach((name) => window.removeEventListener(name, handleAuthChange));
    };
  }, []);

  return {
    isAuthenticated,
    userProfile,
    storedDisplayName,
    refreshAuth // Export for manual refresh if needed
  };
}
